import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Sparkles, Calendar, Package, Percent, RefreshCw, Lightbulb, Gift, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const PROMOTION_TYPES = [
  { id: 'general', name: 'Geral', icon: Lightbulb, description: 'Promoções baseadas nos seus produtos' },
  { id: 'seasonal', name: 'Sazonal', icon: Calendar, description: 'Aproveite datas comemorativas' },
  { id: 'combo', name: 'Combos', icon: Package, description: 'Kits e vendas casadas' },
  { id: 'clearance', name: 'Queima', icon: Percent, description: 'Gire o estoque parado' }
];

const Promotions = () => {
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('general');
  const [suggestions, setSuggestions] = useState(null);
  const [currentMonth, setCurrentMonth] = useState('');

  const generatePromotions = async () => {
    setLoading(true);
    setSuggestions(null);
    
    try {
      const res = await axios.post(`${API}/ai/promotions`, {
        prompt_type: selectedType
      });
      
      setSuggestions(res.data.suggestions);
      setCurrentMonth(res.data.month);
      toast.success('Promoções geradas com sucesso!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar promoções');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Promoções com IA">
      <div className="space-y-6">
        {/* Header */}
        <Card className="card-adegga border-[#ff6b00]/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#ff6b00] to-[#ff8533] flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Assistente de Promoções</h2>
                <p className="text-white/60">
                  Use inteligência artificial para criar promoções inteligentes e econômicas baseadas nos seus produtos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Promotion Types */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {PROMOTION_TYPES.map(type => (
            <Card
              key={type.id}
              className={`cursor-pointer transition-all ${
                selectedType === type.id
                  ? 'card-adegga border-[#ff6b00] ring-2 ring-[#ff6b00]/50'
                  : 'card-adegga border-white/10 hover:border-white/20'
              }`}
              onClick={() => setSelectedType(type.id)}
            >
              <CardContent className="p-4 text-center">
                <type.icon className={`w-8 h-8 mx-auto mb-2 ${
                  selectedType === type.id ? 'text-[#ff6b00]' : 'text-white/40'
                }`} />
                <h3 className={`font-medium ${
                  selectedType === type.id ? 'text-white' : 'text-white/60'
                }`}>{type.name}</h3>
                <p className="text-xs text-white/40 mt-1">{type.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Generate Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={generatePromotions}
            disabled={loading}
            className="bg-gradient-to-r from-[#ff6b00] to-[#ff8533] hover:from-[#ff8533] hover:to-[#ff9955] text-white px-8 py-6 text-lg"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Gerando ideias...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                Gerar Promoções
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        {suggestions && (
          <Card className="card-adegga">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#ff6b00]" />
                Sugestões de Promoção
                {currentMonth && (
                  <span className="text-sm font-normal text-white/40 ml-2">
                    ({currentMonth})
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-white/80 leading-relaxed">
                  {suggestions}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="card-adegga bg-[#ff6b00]/5 border-[#ff6b00]/20">
          <CardContent className="p-4">
            <h4 className="text-[#ff6b00] font-medium mb-2 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Dicas para promoções eficazes
            </h4>
            <ul className="text-sm text-white/60 space-y-1">
              <li>• Crie senso de urgência com prazos curtos (ex: "Só até domingo!")</li>
              <li>• Combos aumentam o ticket médio sem dar desconto direto</li>
              <li>• Use datas comemorativas como gatilho de compra</li>
              <li>• Promoções de frete grátis acima de X valor incentivam compras maiores</li>
              <li>• Divulgue no WhatsApp Status e redes sociais</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Promotions;
